// Pointer-based calendar drag-and-drop wiring (replaces native HTML5 DnD, which
// is dead in the office Edge). Owns ONE DndContext config + the four lifted
// mutations, dispatched by `resolveCalendarDrop`. Child components are now pure
// useDraggable/useDroppable wrappers.
//
// The four mutations were lifted verbatim from the child components:
//   reassign-dates  = patchCalendarAssignment  (month patchReassign ≡ week patchReschedule)
//   task-move       = patchScheduledTask        (week superset; people optional → month behaviour)
//   new-assignment  = postCalendarAssignment    (week dropMutation; optimistic rebuilt from active.data)
//   unassign        = deleteCalendarAssignment  (sidebar)
import { useState } from 'react';
import {
  PointerSensor,
  KeyboardSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AssignmentPurpose,
  CalendarAssignmentRow,
  CalendarTaskRow,
  CalendarUnassignedResponse,
} from '@gutterinv/shared';
import {
  patchCalendarAssignment,
  patchScheduledTask,
  postCalendarAssignment,
  deleteCalendarAssignment,
} from '../api/client.js';
import { toISO } from '../lib/calendar-dates.js';
import { resolveCalendarDrop, type DropTarget } from '../lib/calendar-drop.js';
import type { CalendarDragPayload } from '../lib/calendar-dates.js';

/** Display fields the unassigned card carries so the new-assignment optimistic
 *  placeholder shows real text immediately (no scavenger lookup). */
export interface DragJobDetails {
  displayName: string;
  address: string;
  customerName: string;
  quoteAmountCents: number | null;
}

/** What every calendar draggable puts on its `data`. */
export interface CalendarDragData {
  payload: CalendarDragPayload;
  overlayLabel: string;
  job?: DragJobDetails; // present only for kind:'unassigned'
}

export interface CrewPickerRequest {
  jobId: string;
  dateISO: string;
  purpose: AssignmentPurpose;
  durationDays: number;
}

export function useCalendarDnd(opts: { onCrewPicker: (req: CrewPickerRequest) => void }) {
  const queryClient = useQueryClient();
  const [activeDrag, setActiveDrag] = useState<CalendarDragData | null>(null);

  // distance:6 so a plain click still fires the chip/card onClick (open detail /
  // expand / select); a drag needs ≥6px movement. KeyboardSensor for a11y.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  // reassign-dates — patch an existing assignment's date span in place (people +
  // notes + completedAt preserved). Identical optimistic for month & week.
  const reassignMutation = useMutation({
    mutationFn: ({ id, startDate, endDate }: { id: string; startDate: string; endDate: string }) =>
      patchCalendarAssignment(id, { startDate, endDate }),
    onMutate: async ({ id, startDate, endDate }) => {
      await queryClient.cancelQueries({ queryKey: ['calendar'] });
      const prevAssignments = queryClient.getQueriesData<CalendarAssignmentRow[]>({
        queryKey: ['calendar', 'assignments'],
      });
      queryClient.setQueriesData<CalendarAssignmentRow[]>(
        { queryKey: ['calendar', 'assignments'] },
        (old) =>
          old?.map((a) =>
            a.id === id ? { ...a, assignedDateStart: startDate, assignedDateEnd: endDate } : a,
          ) ?? [],
      );
      return { prevAssignments };
    },
    onError: (_e, _v, ctx) => {
      ctx?.prevAssignments?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  // task-move — single-day reschedule; when `people` is set (single-assignee
  // dropped on a different person row) it also reassigns. Month passes no people.
  const taskMutation = useMutation({
    mutationFn: ({
      id,
      taskDate,
      people,
    }: {
      id: string;
      taskDate: string;
      people?: { id: string; name: string }[];
    }) => patchScheduledTask(id, people ? { taskDate, personIds: people.map((p) => p.id) } : { taskDate }),
    onMutate: async ({ id, taskDate, people }) => {
      await queryClient.cancelQueries({ queryKey: ['calendar'] });
      const prevTasks = queryClient.getQueriesData<CalendarTaskRow[]>({ queryKey: ['calendar', 'tasks'] });
      queryClient.setQueriesData<CalendarTaskRow[]>(
        { queryKey: ['calendar', 'tasks'] },
        (old) => old?.map((t) => (t.id === id ? { ...t, taskDate, ...(people ? { people } : {}) } : t)) ?? [],
      );
      return { prevTasks };
    },
    onError: (_e, _v, ctx) => {
      ctx?.prevTasks?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });

  // new-assignment — create an install/estimate assignment when an unassigned
  // sidebar card is dropped on a week person-day. Optimistic placeholder is built
  // from the data the dragged card carried (`_opt`) — no scavenger lookup.
  const assignMutation = useMutation({
    mutationFn: ({
      jobId,
      personIds,
      startDate,
      endDate,
      purpose,
    }: {
      jobId: string;
      personIds: string[];
      startDate: string;
      endDate: string;
      purpose: AssignmentPurpose;
      _opt: { person: { id: string; name: string }; job: DragJobDetails };
    }) => postCalendarAssignment({ jobId, personIds, startDate, endDate, purpose }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['calendar'] });
      const prevAssignments = queryClient.getQueriesData<CalendarAssignmentRow[]>({
        queryKey: ['calendar', 'assignments'],
      });
      const prevUnassigned = queryClient.getQueryData<CalendarUnassignedResponse>(['calendar', 'unassigned']);
      const purpose = input.purpose;
      const optimistic: CalendarAssignmentRow = {
        id: `optimistic-${Date.now()}`,
        assignedDateStart: input.startDate,
        assignedDateEnd: input.endDate,
        notes: null,
        purpose,
        completedAt: null,
        crew: { id: '', name: '', color: '#94a3b8' },
        people: [input._opt.person],
        job: {
          id: input.jobId,
          displayName: input._opt.job.displayName,
          address: input._opt.job.address,
          status: purpose === 'estimate' ? 'created' : 'accepted',
          customerName: input._opt.job.customerName,
          quoteAmountCents: input._opt.job.quoteAmountCents,
          hasInstallAssignment: purpose === 'install',
        },
      };
      queryClient.setQueriesData<CalendarAssignmentRow[]>(
        { queryKey: ['calendar', 'assignments'] },
        (old) => (old ? [...old, optimistic] : [optimistic]),
      );
      if (prevUnassigned) {
        queryClient.setQueryData<CalendarUnassignedResponse>(['calendar', 'unassigned'], (old) =>
          old
            ? {
                installs: old.installs.filter((j) => j.id !== input.jobId),
                estimates: old.estimates.filter((j) => j.id !== input.jobId),
              }
            : old,
        );
      }
      return { prevAssignments, prevUnassigned };
    },
    onError: (_e, _input, ctx) => {
      ctx?.prevAssignments?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      if (ctx?.prevUnassigned) {
        queryClient.setQueryData<CalendarUnassignedResponse>(['calendar', 'unassigned'], ctx.prevUnassigned);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  // unassign — drop an assignment chip on the sidebar.
  const unassignMutation = useMutation({
    mutationFn: deleteCalendarAssignment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
    },
  });

  const onDragStart = (e: DragStartEvent) => {
    setActiveDrag((e.active.data.current as CalendarDragData | undefined) ?? null);
  };
  const onDragCancel = () => setActiveDrag(null);

  const onDragEnd = (e: DragEndEvent) => {
    setActiveDrag(null);
    const data = e.active.data.current as CalendarDragData | undefined;
    if (!data) return;
    const target = (e.over?.data.current as DropTarget | undefined) ?? null;
    const result = resolveCalendarDrop(data.payload, target, toISO(new Date()));
    if (result.action === null) return; // result.reason available for debugging

    switch (result.action) {
      case 'reassign-dates':
        reassignMutation.mutate({ id: result.assignmentId, startDate: result.startDate, endDate: result.endDate });
        break;
      case 'task-move':
        taskMutation.mutate({ id: result.taskId, taskDate: result.taskDate, people: result.people });
        break;
      case 'unassign':
        unassignMutation.mutate(result.assignmentId);
        break;
      case 'crew-picker':
        opts.onCrewPicker({ jobId: result.jobId, dateISO: result.iso, purpose: result.purpose, durationDays: result.durationDays });
        break;
      case 'new-assignment': {
        // resolver only returns this for a person-day target
        const pd = target as Extract<DropTarget, { kind: 'person-day' }>;
        assignMutation.mutate({
          jobId: result.jobId,
          personIds: result.personIds,
          startDate: result.startDate,
          endDate: result.endDate,
          purpose: result.purpose,
          _opt: {
            person: { id: pd.personId, name: pd.personName },
            job: data.job ?? { displayName: '…', address: '', customerName: '', quoteAmountCents: null },
          },
        });
        break;
      }
    }
  };

  return { sensors, collisionDetection: pointerWithin, onDragStart, onDragEnd, onDragCancel, activeDrag };
}
